import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Mail, Lock, ArrowRight, AlertCircle, Eye, EyeOff, Fingerprint, Check } from 'lucide-react';

const PLATFORM_ROLES = new Set(['superadmin', 'vice_superadmin', 'amministratore_piattaforma']);

const BRAND_BULLETS = [
  "Risponde al telefono 24 ore su 24",
  "Richiama i lead che non ti hanno trovato",
  "Prenota appuntamenti direttamente in agenda",
];

const EliosLoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (user?.role === 'elios_client') {
      navigate('/elios/dashboard', { replace: true });
    } else if (user?.role && PLATFORM_ROLES.has(user.role)) {
      navigate('/admin/elios-system', { replace: true });
    } else if (user?.role === 'distributore') {
      navigate('/distributor/dashboard', { replace: true });
    }
  }, [user, navigate]);

  // Impersonation shortcut: the distributor's dashboard opens a new tab with
  // `?impersonate=<jwt>&email=<target>` so the platform boots straight into
  // the client account without a password prompt. The token is short-lived
  // (15 min) and already logged server-side.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const impToken = params.get('impersonate');
    if (!impToken) return;
    try {
      sessionStorage.setItem('token', impToken);
      sessionStorage.setItem('impersonation_active', '1');
      sessionStorage.setItem('impersonation_expires_at', String(Date.now() + 15 * 60 * 1000));
    } catch { /* ignore storage errors */ }
    const clean = window.location.pathname;
    window.history.replaceState({}, document.title, clean);
    setTimeout(() => window.location.assign('/elios/dashboard'), 100);
  }, []);

  useEffect(() => {
    if (location.state?.error) {
      setError(location.state.error);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await login(email, password, true);
      const u = res.user || user;
      if (u?.role === 'elios_client') {
        toast.success('Accesso effettuato!');
        navigate('/elios/dashboard', { replace: true });
        return;
      }
      if (u?.role && PLATFORM_ROLES.has(u.role)) {
        toast.success('Accesso admin effettuato!');
        navigate('/admin/elios-system', { replace: true });
        return;
      }
      if (u?.role === 'distributore') {
        toast.success('Accesso distributore effettuato!');
        navigate('/distributor/dashboard', { replace: true });
        return;
      }
      if (!u?.role) {
        setError('Impossibile determinare il ruolo utente');
        toast.error('Accesso non autorizzato');
        return;
      }
      setError('Questo accesso non è autorizzato');
      toast.error('Accesso non autorizzato');
    } catch (err) {
      setError(err.response?.data?.detail || 'Credenziali non valide');
    } finally {
      setLoading(false);
    }
  };

  const handlePasskeyLogin = async () => {
    if (!email) {
      toast.error('Inserisci prima la tua email');
      return;
    }
    setPasskeyLoading(true);
    setError('');
    try {
      const optionsRes = await api.post('/elios-client/webauthn/login-options', { email });
      const { options, user_id } = optionsRes.data;

      const challenge = Uint8Array.from(
        atob(options.challenge.replace(/-/g, '+').replace(/_/g, '/')),
        c => c.charCodeAt(0)
      );

      const allowCredentials = (options.allowCredentials || []).map(c => ({
        type: c.type,
        id: Uint8Array.from(atob(c.id.replace(/-/g, '+').replace(/_/g, '/')), ch => ch.charCodeAt(0)),
      }));

      const assertion = await navigator.credentials.get({
        publicKey: {
          challenge,
          rpId: options.rpId,
          allowCredentials,
          timeout: options.timeout,
          userVerification: options.userVerification,
        },
      });

      const credentialId = btoa(String.fromCharCode(...new Uint8Array(assertion.rawId)))
        .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');

      const verifyRes = await api.post('/elios-client/webauthn/login-verify', {
        user_id,
        id: credentialId,
        rawId: credentialId,
        response: {
          authenticatorData: btoa(String.fromCharCode(...new Uint8Array(assertion.response.authenticatorData))),
          clientDataJSON: btoa(String.fromCharCode(...new Uint8Array(assertion.response.clientDataJSON))),
          signature: btoa(String.fromCharCode(...new Uint8Array(assertion.response.signature))),
        },
      });

      localStorage.setItem('token', verifyRes.data.token);
      toast.success('Accesso con passkey riuscito!');
      window.location.href = '/elios/dashboard';
    } catch (err) {
      if (err.name === 'NotAllowedError') {
        setError('Autenticazione annullata');
      } else {
        setError(err.response?.data?.detail || 'Errore passkey');
      }
    } finally {
      setPasskeyLoading(false);
    }
  };

  const handleForgotSubmit = async () => {
    if (!forgotEmail) return;
    setForgotLoading(true);
    try {
      await api.post('/elios-client/forgot-password', { email: forgotEmail });
      toast.success('Istruzioni di recupero inviate!');
      setShowForgot(false);
      setForgotEmail('');
    } catch (err) {
      const detail = err?.response?.data?.detail || '';
      if (err?.response?.status === 404 || detail.toLowerCase().includes('non presente')) {
        setError('E-mail non presente sul nostro sistema');
        setShowForgot(false);
      } else {
        toast.error('Errore, riprova');
      }
    }
    setForgotLoading(false);
  };

  return (
    <div className="min-h-screen flex bg-white">
      {/* Left brand panel — hidden on mobile */}
      <aside
        aria-hidden="true"
        style={{ backgroundColor: '#374843' }}
        className="hidden lg:flex lg:w-1/2 text-white p-12 flex-col justify-between relative overflow-hidden"
      >
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-emerald-400/20 blur-3xl" />
        <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-emerald-300/10 blur-3xl" />

        <div className="relative">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 hover:opacity-90 transition-opacity"
            aria-label="Vai alla home"
          >
            <img src="/elios-logo-new.svg" alt="Elios" className="h-[4.5rem] w-auto" />
            <span className="text-xs uppercase tracking-wider opacity-80">AI Voice CRM</span>
          </button>
        </div>

        <div className="relative max-w-md">
          <h2 className="text-4xl xl:text-5xl font-bold leading-tight">
            Il tuo assistente vocale, sempre al lavoro.
          </h2>
          <p className="mt-4 text-emerald-50/90 text-lg">
            Elios parla con i tuoi clienti e prende appuntamenti, così tu puoi lavorare senza distrazioni.
          </p>
          <ul className="mt-8 space-y-3">
            {BRAND_BULLETS.map(text => (
              <li key={text} className="flex items-start gap-3">
                <span className="mt-0.5 w-5 h-5 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                  <Check className="w-3.5 h-3.5" />
                </span>
                <span className="text-emerald-50">{text}</span>
              </li>
            ))}
          </ul>
        </div>

        <p className="relative text-xs text-emerald-100/70">
          Powered by Sqlabs
        </p>
      </aside>

      {/* Right form panel */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md">
          {/* Mobile-only logo + back to landing */}
          <div className="lg:hidden mb-8 flex items-center justify-between">
            <button onClick={() => navigate('/')} className="flex items-center gap-2">
              <img src="/elios-logo-new.svg" alt="Elios" className="h-[4.5rem] w-auto" />
              <span className="text-xs uppercase tracking-wider text-zinc-500">AI Voice CRM</span>
            </button>
          </div>

          <div className="mb-8">
            <h1 className="text-3xl font-bold tracking-tight text-zinc-900">Accedi al tuo spazio</h1>
            <p className="mt-2 text-zinc-600 text-sm">Inserisci le credenziali fornite per entrare nella piattaforma.</p>
          </div>

          {error && (
            <div
              className="flex items-center gap-2 p-3 mb-5 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm"
              data-testid="elios-login-error"
            >
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="elios-login-form">
            <div className="space-y-1.5">
              <Label htmlFor="login-email" className="text-zinc-700">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                <Input
                  id="login-email"
                  type="email"
                  placeholder="nome@azienda.it"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 h-11"
                  data-testid="elios-login-email"
                  required
                  autoComplete="email"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="login-password" className="text-zinc-700">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                <Input
                  id="login-password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="La tua password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 pr-10 h-11"
                  data-testid="elios-login-password"
                  required
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600 transition-colors"
                  tabIndex={-1}
                  aria-label={showPassword ? 'Nascondi password' : 'Mostra password'}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold gap-2"
              disabled={loading}
              data-testid="elios-login-submit"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>Accedi <ArrowRight className="w-4 h-4" /></>
              )}
            </Button>
          </form>

          {typeof window !== 'undefined' && window.PublicKeyCredential && (
            <>
              <div className="relative my-5">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-zinc-200" /></div>
                <div className="relative flex justify-center text-xs">
                  <span className="px-2 bg-white text-zinc-500">oppure</span>
                </div>
              </div>
              <Button
                type="button"
                variant="outline"
                className="w-full h-11 gap-2"
                onClick={handlePasskeyLogin}
                disabled={passkeyLoading}
                data-testid="elios-passkey-login"
              >
                {passkeyLoading ? (
                  <div className="w-5 h-5 border-2 border-zinc-400 border-t-transparent rounded-full animate-spin" />
                ) : (
                  <><Fingerprint className="w-5 h-5" /> Accedi con Passkey</>
                )}
              </Button>
            </>
          )}

          <div className="text-center mt-6">
            <button
              onClick={() => setShowForgot(true)}
              className="text-sm text-emerald-700 hover:text-emerald-800 hover:underline transition-colors"
              data-testid="elios-forgot-password-link"
            >
              Hai dimenticato la password?
            </button>
          </div>

          {showForgot && (
            <div className="mt-4 p-4 rounded-lg bg-emerald-50 border border-emerald-100 space-y-3">
              <p className="text-sm text-zinc-700">Inserisci la tua email per ricevere le istruzioni di recupero.</p>
              <Input
                type="email"
                value={forgotEmail}
                onChange={e => setForgotEmail(e.target.value)}
                placeholder="La tua email"
                className="h-10"
                data-testid="elios-forgot-email"
              />
              <Button
                size="sm"
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                disabled={forgotLoading}
                onClick={handleForgotSubmit}
                data-testid="elios-forgot-submit"
              >
                {forgotLoading ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : 'Invia'}
              </Button>
            </div>
          )}

          <p className="text-center text-zinc-400 text-xs mt-8">
            Piattaforma riservata ai clienti Elios AI
          </p>
        </div>
      </div>
    </div>
  );
};

export default EliosLoginPage;
